import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { HrDeductionsResponse } from '../../../../models/api/view/claim/hr/deductions/hr-deductions-response';
import { ExportService } from '../../../../services/export/export.service';
import { ClaimExportMapper } from '../../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../../base/base.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { FormatPipe } from '../../../../pipes/format.pipe';

@Component({
  selector: 'app-deductions',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    DetailPanelComponent,
    DetailItemComponent,
    CardComponent,
    FormatPipe,
  ],
  providers: [CurrencyPipe],
  template: `
    <app-page-header label="Deductions/Liens" labelCase="none" />

    <app-table
      [dataSource]="items"
      [pageSize]="10"
      [scrollable]="false"
      dataKey="hrDeductUid"
      recordLabel="Deduction"
      [expandable]="true"
      [stripedRows]="false"
      [showExport]="true"
      (export)="onExport($event)"
    >
      <app-column name="deductionType" label="type" classes="tw-font-bold" />
      <app-column name="amount" label="deduction">
        <ng-template let-data>
          @if (data.deductionIsInPercent) {
            {{ data.amount | format: '%' }}
          } @else {
            {{ data.amount | format: 'c' }}
          }
        </ng-template>
      </app-column>
      <app-column name="frequency" label="frequency" />
      <app-column name="inceptionDate" label="inception" format="d" />
      <app-column name="expirationDate" label="expiration" format="d" />

      <ng-template #expand let-data>
        <div class=" tw-flex tw-flex-col tw-gap-4 tw-py-4">
          <app-card label="deductions">
            <div class="tw-flex tw-gap-8">
              <div class="tw-basis-1/3">
                <app-detail-panel [dataSource]="data" [showEmpty]="true">
                  <app-detail-item
                    label="adjustment Group"
                    name="adjustmentGroup"
                  />
                  <app-detail-item label="subType" name="subType" />
                  <app-detail-item
                    label="inception Date"
                    name="inceptionDate"
                    format="d"
                  />
                  <app-detail-item
                    label="partial Flag"
                    name="partialFlag"
                    format="b"
                  />
                </app-detail-panel>
              </div>

              <div class="tw-basis-1/3">
                <app-detail-panel [dataSource]="data" [showEmpty]="true">
                  <app-detail-item
                    label="deduction Type"
                    name="deductionType"
                  />
                  <app-detail-item label="frequency" name="frequency" />
                  <app-detail-item
                    label="expiration Date"
                    name="expirationDate"
                    format="d"
                  />
                  <app-detail-item
                    label="calculation Method"
                    name="calculationMethod"
                  />
                </app-detail-panel>
              </div>

              <div class="tw-basis-1/3">
                <app-detail-panel [dataSource]="data" [showEmpty]="true">
                  <app-detail-item
                    label="deduction is in %"
                    name="deductionIsInPercent"
                    format="b"
                  />
                  @if (data.deductionIsInPercent) {
                    <app-detail-item label="amount" name="amount" format="%" />
                  } @else {
                    <app-detail-item label="amount" name="amount" format="c" />
                  }
                  <app-detail-item label="priority Code" name="priorityCode" />
                  <app-detail-item label="age Reduction" name="ageReduction" />
                </app-detail-panel>
              </div>
            </div>
          </app-card>

          <app-card label="lien information">
            <div class="tw-flex tw-gap-8">
              <div class="tw-basis-1/3">
                <app-detail-panel [dataSource]="data" [showEmpty]="true">
                  <app-detail-item
                    label="adjustment Group"
                    name="adjustmentGroup"
                  />
                  <app-detail-item label="lien SubType" name="lienSubType" />
                  <app-detail-item label="name" name="name" />
                  <app-detail-item label="earnings Type" name="earningsType" />
                  <app-detail-item label="pay To" name="payTo" />
                  <app-detail-item
                    label="service Fee %"
                    name="serviceFeePercent"
                  />
                  <app-detail-item
                    label="Effective Date"
                    name="effectiveDate"
                    format="d"
                  />
                </app-detail-panel>
              </div>

              <div class="tw-basis-1/3">
                <app-detail-panel [dataSource]="data" [showEmpty]="true">
                  <app-detail-item
                    label="Lien ID"
                    name="lienId"
                    labelCase="none"
                  />
                  <app-detail-item label="fein" name="fein" labelCase="upper" />
                  <app-detail-item label="goal Amount" name="goalAmount" />
                  <app-detail-item label="exemptions" name="exemptions" />
                  <app-detail-item
                    label="marital Status"
                    name="maritalStatus"
                  />
                </app-detail-panel>
              </div>

              <div class="tw-basis-1/3">
                <app-detail-panel [dataSource]="data" [showEmpty]="true">
                  <app-detail-item label="court State" name="courtState" />
                  <app-detail-item label="preTax" name="preTax" format="b" />
                  <app-detail-item label="fips" name="fips" labelCase="upper" />
                  <app-detail-item label="service Fee" name="serviceFee" />
                  <app-detail-item
                    label="lien Serve Date"
                    name="lienServeDate"
                    format="d"
                  />
                </app-detail-panel>
              </div>
            </div>
          </app-card>
        </div>
      </ng-template>
    </app-table>
  `,
  styles: ``,
})
export class DeductionsComponent extends BaseComponent implements OnInit {
  exportService = inject(ExportService);
  private datePipe = inject(DatePipe);
  private currencyPipe = inject(CurrencyPipe);

  items: HrDeductionsResponse[] = [];

  async ngOnInit() {
    this.items = await this.api.deductions.deductions(getClaimKey());
    this.view.hideLoading();
  }

  onExport(exportType: ExportFormat) {
    const rows = this.items.map((item) => ({
      deductionType: item.deductionType,
      amount: this.currencyPipe.transform(
        item.amount,
        'USD',
        'symbol',
        '1.2-2',
      ),
      frequency: item.frequency,
      inceptionDate:
        item.inceptionDate === '0001-01-01T00:00:00'
          ? null
          : item.inceptionDate,
      expirationDate:
        item.expirationDate === '0001-01-01T00:00:00'
          ? null
          : item.expirationDate,
    }));

    const columns = [
      { propertyName: 'deductionType', headerText: 'Type' },
      { propertyName: 'amount', headerText: 'Deduction' },
      { propertyName: 'frequency', headerText: 'Frequency' },
      {
        propertyName: 'inceptionDate',
        headerText: 'Inception',
      },
      {
        propertyName: 'expirationDate',
        headerText: 'Expiration',
      },
    ];

    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `HRDeductions_${formattedDate}.${exportType}`;

    if (exportType === 'csv') {
      const csvData = ClaimExportMapper.toCsvExport(
        rows,
        columns,
        'HrDeductions',
      );
      this.exportService.exportCsv(csvData, filename);
    } else if (exportType === 'xlsx') {
      const excelData = ClaimExportMapper.toExcelExport(
        rows,
        columns,
        'HrDeductions',
        'OmniInsights Export',
      );
      this.exportService.exportExcel(excelData, filename);
    }
  }
}
